import ComingSoon from "@/components/homepage/ComingSoon";
import { RoundCard } from "@/components/homepage/RoundCard";
import SelectDetailTab from "@/components/rounds/SelectDetailTab";
import { useHandleError } from "@/hooks/useHandleError";
import { useSkipPrizesStore } from "@/store";
import { useSoccerRound } from "@roman/shared/hooks/soccer/useSoccerRound";
import type { Round } from "@roman/shared/types/Round";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ContentInfo, ContentPrize, ContentRule, LoadingSpinner, RoundDetailPlayButton } from ".";
import { TABS, type Tab } from "./constant";

export const Prizes = () => {
  return (
    <div className="w-full">
      <PrizePage />
    </div>
  );
};

const PrizePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roundId } = useParams<{ roundId: string }>();
  if (!roundId) {
    throw new Error("roundId is required");
  }
  const { skipPrizes, setSkipPrizes } = useSkipPrizesStore();

  const roundDetailHandleError = useHandleError("ラウンドの詳細取得に失敗しました");

  const { data: roundData, isLoading: isRoundLoading } = useSoccerRound({
    roundId,
    lang: "ja",
    swrOptions: {
      onError: roundDetailHandleError,
    },
  });
  const [selectedTab, setSelectedTab] = useState<Tab>(TABS[0]);

  const selectedRound = useMemo(() => {
    if (!roundData?.round) {
      return null;
    }

    const round: Round = {
      ...roundData.round.roundEntryUnits[0],
      ...roundData.round,
    };
    return round;
  }, [roundData]);

  const handleGotoSelectPlayers = () => {
    if (selectedRound && selectedRound.status === "Open" && selectedRound.entryCloseTime > Date.now()) {
      navigate(`/rounds/${roundId}/select-players`);
    }
  };

  const SelectedContent = ({ round }: { round: Round }) => {
    switch (selectedTab.type) {
      case "info":
        return <ContentInfo />;
      case "rule": {
        return <ContentRule />;
      }
      case "prize":
        return <ContentPrize round={round} />;

      default:
        return null;
    }
  };
  if (isRoundLoading || !selectedRound) {
    return (
      <div className="w-full h-screen flex justify-center items-center bg-blue">
        <LoadingSpinner />
      </div>
    );
  }

  const matchName = `${selectedRound.homeName} vs ${selectedRound.awayName}`;
  const roundDetailPlayButtonText = location.state?.roundDetailPlayButtonText;

  const SkipPrizesCheckbox = () => (
    <>
      <button
        type="button"
        onClick={() => navigate(`/rounds/${selectedRound.id}/select-players`)}
        className="bg-gradient-primary text-white font-bold px-4 py-2 rounded-md w-full"
      >
        エントリーする
      </button>
      <div className="my-4 flex justify-center">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="skipPrizes"
            className="w-5 h-5 accent-primary"
            checked={skipPrizes}
            onChange={(e) => setSkipPrizes(e.target.checked)}
          />
          <label htmlFor="skipPrizes" className="text-[10px] sm:text-xs text-dark-gray leading-4 sm:leading-5">
            次回からこの画面はスキップし、選手選択画面からスタートする。
            <br />
            ※賞金は大会情報画面でいつでも確認できます。
          </label>
        </div>
      </div>
    </>
  );

  return (
    <div className="w-full bg-white pb-4">
      <div className="flex justify-center items-center p-[12px] bg-blue">
        <p className="font-bold text-xs whitespace-nowrap overflow-hidden text-primary text-center">{matchName}</p>
      </div>
      <div className="m-2">
        <RoundCard isRounded={false} round={selectedRound} onClick={handleGotoSelectPlayers} />
        <RoundDetailPlayButton
          roundDetailPlayButtonText={roundDetailPlayButtonText}
          handleGotoSelectPlayers={handleGotoSelectPlayers}
        />
        <div className="mt-5">
          <SkipPrizesCheckbox />
        </div>
        <div className="border-t border-t-[#ECECEC] border-b border-b-[#ADADAD]">
          <SelectDetailTab selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
        </div>

        {isRoundLoading ? (
          <LoadingSpinner />
        ) : selectedRound ? (
          <div className="mx-2">
            <SelectedContent round={selectedRound} />
          </div>
        ) : (
          <ComingSoon />
        )}
        <SkipPrizesCheckbox />
      </div>
    </div>
  );
};
